import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

import { UNLINKED_MNCMS_COUNT_QUERY_KEY } from "./useUnlinkedMncmsCount";
import { UNLINKED_MNCMS_QUERY_KEY } from "./useUnlinkedMncmsMessages";

/**
 * Attaches a needs-attention message to a patient, and optionally to one of that
 * patient's pregnancies. No patient and no pregnancy are ever created here — filing
 * a hospital's clinical record against a chart off an unverified identity match is a
 * decision for a person, not for this form.
 *
 * The server's 400/409 bodies are written to be read by a user ("Message has already
 * been signed off and cannot be re-assigned"), so they are shown as they arrive
 * rather than replaced with a generic line.
 */
export const useAssignMncmsInboundMessage = () => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant") ?? "";

  return useMutation({
    mutationFn: async ({ id, patientId, pregnancyId }) => {
      const axios = createAxiosInstance();
      const response = await axios.post(
        `${tenantVal}/mncms/inbound-messages/${id}/assign`,
        // Omitted rather than sent as null when there's no pregnancy yet: the
        // field is optional, and assigning again later is the supported path.
        pregnancyId ? { patientId, pregnancyId } : { patientId }
      );

      return response.data?.data ?? response.data;
    },
    onSuccess: () => {
      toast.success("Message assigned.");
      queryClient.invalidateQueries({ queryKey: [UNLINKED_MNCMS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [UNLINKED_MNCMS_COUNT_QUERY_KEY],
      });
      // Attaching the pregnancy is what releases the outbound send gate, so the
      // pregnancy views have to be re-read too.
      queryClient.invalidateQueries({ queryKey: ["mncmsInboundMessages"] });
      queryClient.invalidateQueries({ queryKey: ["pregnancies"] });
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "Could not assign the message. Please try again."
      );
    },
    meta: { skipGlobalError: true },
  });
};
